/*
 * Compare products tray + modal.
 *
 * State lives in localStorage so a selection survives navigating between
 * collection, search and product pages. Every read and write is wrapped:
 * storage throws in Safari private mode, and a customer with it blocked
 * should still get a working (just non-persistent) compare.
 *
 * Toggle changes are handled by delegation on document, not by binding to
 * each checkbox, because Dawn re-renders the product grid from scratch when
 * facets or sorting change. Delegation means the new checkboxes work without
 * rebinding, and syncToggles() re-ticks the ones still in the tray.
 *
 * Specs come from the card's data attribute, captured server-side from
 * metafields. On open we re-fetch /products/<handle>.js purely to refresh
 * price and availability - stock moves fast enough here that a machine can
 * sell while it is sitting in someone's tray.
 *
 * NOTE FOR ANYONE EDITING THE STRINGS BELOW: keep apostrophes out of the
 * single-quoted literals. This file is deployed through a GraphQL block
 * string, which passes backslashes through untouched, so an escaped
 * apostrophe arrives as a stray backslash and breaks the whole file.
 * Copy that needs one can use a double-quoted string instead.
 *
 * WHAT SHAPES THIS PANEL
 * 1. It does NOT recommend one machine. It says what each is suited to and
 *    better at, and leaves the choice alone, because the weight of a
 *    scratch against 8GB of RAM is a personal call, not a calculable one.
 * 2. Strengths are scored against EACH rival, not the whole field. An
 *    earlier version only credited the outright winner, so a sensible
 *    middle option - newer than the cheap machine, far cheaper than the
 *    fast one - was told it led on nothing, which is exactly backwards.
 * 3. At three machines a win that does not lead the field NAMES what it
 *    beats, because "Lower price" beside another panel reading "Lowest
 *    price" cannot be told apart. At two, every win is outright and needs
 *    no qualifier, which is why two is the default.
 * 4. Specs that match are marked "Same", so a shopper can see at a glance
 *    which rows are the actual decision and which are not.
 * 5. Every section lines up across panels - see the CSS for how, and
 *    renderPanels for the custom properties that drive it.
 * 6. Only the three most decision-relevant strengths are shown, and
 *    laptops never mix with desktops.
 */

const STORAGE_KEY = 'vg:compare';

/* How many ticks a panel may show. See point 6 above. */
const MAX_WINS = 3;

/* Nothing to compare below this, so the button stays out of reach. */
const MIN_TO_COMPARE = 2;

/*
 * Sections in a panel that are not spec rows: media, title, price, the
 * Ideal-for heading, its tags, the Better-here heading, the strengths list,
 * and the button. The CSS builds one grid row per section, so this has to
 * stay in step with what renderPanels appends.
 */
const PANEL_FIXED_ROWS = 8;

/* Stripped from the front of a rival name so "than the ..." stays short. */
const VENDOR_WORDS = [
  'dell',
  'hp',
  'lenovo',
  'apple',
  'microsoft',
  'acer',
  'asus',
  'msi',
  'dynabook',
  'toshiba',
];

/*
 * Keyed by the classes the build-class snippet produces, laptops first then
 * desktop form factors. Deliberately one line each: the panel has to fit
 * beside another. If a class is added there, add it here too - an unknown
 * class renders without a note rather than breaking the row.
 */
const BUILD_NOTES = {
  'Flagship ultraportable': 'Carbon fibre or magnesium, lightest of its generation',
  'Mobile workstation': 'Discrete GPU, built for sustained heavy loads',
  'Premium business': 'Magnesium or aluminium, MIL-STD tested, serviceable',
  'Mainstream business': 'Durable fleet workhorse, heavier, more basic screen',
  'Premium consumer': 'Good materials and screen, built for retail not fleets',
  'Entry business': 'Business features, cost-reduced plastic chassis',
  Consumer: 'Retail plastic chassis, for light home and study use',

  'Workstation tower': 'Room for a workstation graphics card and several drives',
  'Tower desktop': 'The most upgrade room - full-size cards, extra drives, easy access',
  'Small form factor': 'Fits under a desk with some upgrade room, quieter than a tower',
  'Micro desktop': 'Palm-sized and near silent, mounts behind a monitor; RAM and storage only',
  Desktop: 'A standard desktop chassis',
};

/*
 * What each grade means, condensed from the wording already published in
 * every product description, so the compare panel cannot promise something
 * the listing does not.
 *
 * Battery health is a property of the GRADE, not of the individual unit:
 * there is no per-product battery metafield. The percentages are floors
 * ("above 70%"), which is why the row is labelled as a minimum and why a
 * higher one is never described as "better battery life". Desktops are
 * graded on the same scale but have no battery, so that row is suppressed
 * for them rather than quoting a figure that means nothing.
 *
 * If the grading thresholds in the product descriptions are ever changed,
 * change them here too - nothing enforces that they match.
 */
const GRADE_DETAIL = {
  Excellent: { battery: 'Above 80%', batteryFloor: 80, cosmetic: 'Minimal to no marks' },
  'Very Good': { battery: 'Above 70%', batteryFloor: 70, cosmetic: 'Small scratches or dents' },
  Good: { battery: 'Above 50%', batteryFloor: 50, cosmetic: 'Scratches, dents or screen marks; no cracks' },
};

/* Keys come from the product-features snippet. Laptops only in practice. */
const FEATURE_LABELS = {
  touch: { label: 'Touchscreen', lead: 'Touchscreen', detail: '' },
  convertible: { label: 'Tablet mode', lead: 'Folds into a tablet', detail: '360° hinge' },
  detachable: { label: 'Detachable', lead: 'Detachable keyboard', detail: 'screen works alone' },
};

const FEATURE_ORDER = ['touch', 'convertible', 'detachable'];

const CONDITION_RANK = { Good: 1, 'Very Good': 2, Excellent: 3 };

/* ------------------------------------------------------- value parsing -- */

function currencyCode() {
  return window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'AUD';
}

function formatMoney(amount) {
  try {
    return amount.toLocaleString(undefined, { style: 'currency', currency: currencyCode() });
  } catch (e) {
    return '$' + amount.toFixed(2);
  }
}

function parseMoney(value) {
  const n = parseFloat(String(value).replace(/[^0-9.]/g, ''));
  return isNaN(n) ? null : n;
}

/* "16 GB" / "1 TB" -> a comparable number of GB. */
function parseSize(value) {
  const m = String(value).match(/([\d.]+)\s*(tb|gb|mb)/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  const unit = m[2].toLowerCase();
  if (unit === 'tb') return n * 1024;
  if (unit === 'mb') return n / 1024;
  return n;
}

/*
 * Processors are only ranked when every machine is the same family. An Intel
 * generation and an Apple M number do not mean the same thing, so comparing
 * across families would produce a confident wrong answer - better to leave
 * it unranked and let the customer decide.
 */
function parseCpu(value) {
  const s = String(value).toLowerCase();

  const tier = s.match(/\bi([3579])\b/);
  const gen = s.match(/(\d+)\s*(?:th|st|nd|rd)\s*gen/);
  if (tier && gen) {
    const tiers = { 3: 1, 5: 2, 7: 3, 9: 4 };
    /*
     * Generation outranks tier deliberately: an 11th gen i5 beats an 8th gen
     * i7 on single-core and graphics, which is what these machines are used for.
     */
    return { family: 'intel', score: parseInt(gen[1], 10) * 10 + tiers[tier[1]] };
  }

  const apple = s.match(/\bm([1234])\b/);
  if (apple) return { family: 'apple', score: parseInt(apple[1], 10) };

  const ryzen = s.match(/ryzen\s*([3579])/);
  if (ryzen) return { family: 'ryzen', score: parseInt(ryzen[1], 10) };

  return null;
}

/* Windows 10 is past end of support, so a higher number is genuinely better. */
function parseWindows(value) {
  const m = String(value).toLowerCase().match(/windows\s*(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

function gradeDetail(item) {
  return GRADE_DETAIL[item.condition] || null;
}

function isDesktop(item) {
  return item.kind === 'desktop';
}

function featureKeys(item) {
  if (!item.features) return [];
  return String(item.features)
    .split(',')
    .map((key) => key.trim())
    .filter(Boolean);
}

function cpuGeneration(item) {
  const parsed = parseCpu(item.cpu);
  return parsed && parsed.family === 'intel' ? Math.floor(parsed.score / 10) : null;
}

function screenInches(item) {
  const m = String(item.screen || '').match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
}

/*
 * Weight is looked up from the model name against laptop-weights.js, because
 * the variant weight on every laptop is a flat 1.5 kg shipping figure. A
 * model that matches nothing returns null and the row is left out entirely -
 * a guessed weight would be worse than none. Desktops are not in the table.
 */
function weightOf(item) {
  if (isDesktop(item)) return null;
  const table = window.VG_LAPTOP_WEIGHTS || [];
  const model = String(item.model || item.title || '').toLowerCase();
  for (let i = 0; i < table.length; i += 1) {
    if (model.indexOf(table[i][0]) !== -1) return table[i][1];
  }
  return null;
}

/* ------------------------------------------------------------ ideal for -- */

/*
 * What a machine suits, judged on its own rather than against the others -
 * a 13" ThinkPad is good for travel whether or not something heavier is
 * sitting next to it. First two matches win, so each list runs from the
 * most defining trait to the most general.
 *
 * Desktops get their own list because half the laptop tests are meaningless
 * on them: there is no screen to measure and nothing to carry. What matters
 * instead is whether it can be upgraded and how much desk it takes up.
 */
const IDEAL_TAGS = {
  laptop: [
    {
      label: 'Creative & engineering work',
      test: function (item) {
        return item.buildClass === 'Mobile workstation';
      },
    },
    {
      label: 'Heavy multitasking',
      test: function (item) {
        return (parseSize(item.ram) || 0) >= 32;
      },
    },
    {
      label: 'Note-taking & presenting',
      test: function (item) {
        const keys = featureKeys(item);
        return keys.indexOf('convertible') !== -1 || keys.indexOf('detachable') !== -1;
      },
    },
    {
      label: 'Demanding office work',
      test: function (item) {
        return (parseSize(item.ram) || 0) >= 16 && (cpuGeneration(item) || 0) >= 10;
      },
    },
    {
      label: 'Big-screen desk work',
      test: function (item) {
        return (screenInches(item) || 0) >= 15;
      },
    },
    {
      /* Weight decides this when known; screen size is only the fallback. */
      label: 'Travel & commuting',
      test: function (item) {
        const weight = weightOf(item);
        if (weight !== null) return weight <= 1.4;
        return (screenInches(item) || 99) <= 13 && (Number(item.buildScore) || 0) >= 4;
      },
    },
    {
      label: 'Touch-first browsing',
      test: function (item) {
        return featureKeys(item).indexOf('touch') !== -1;
      },
    },
    {
      label: 'Email, web & study',
      test: function (item) {
        return (parseSize(item.ram) || 0) <= 8;
      },
    },
  ],

  desktop: [
    {
      label: 'Creative & engineering work',
      test: function (item) {
        return item.buildClass === 'Workstation tower';
      },
    },
    {
      label: 'Heavy multitasking',
      test: function (item) {
        return (parseSize(item.ram) || 0) >= 32;
      },
    },
    {
      label: 'Tight desk spaces',
      test: function (item) {
        return item.buildClass === 'Micro desktop';
      },
    },
    {
      label: 'Demanding office work',
      test: function (item) {
        return (parseSize(item.ram) || 0) >= 16 && (cpuGeneration(item) || 0) >= 10;
      },
    },
    {
      label: 'Upgrading later on',
      test: function (item) {
        return item.buildClass === 'Tower desktop' || item.buildClass === 'Small form factor';
      },
    },
    {
      label: 'Email, web & study',
      test: function (item) {
        return (parseSize(item.ram) || 0) <= 8;
      },
    },
  ],
};

function idealFor(item) {
  const list = IDEAL_TAGS[item.kind] || IDEAL_TAGS.laptop;
  const tags = list
    .filter((tag) => tag.test(item))
    .map((tag) => tag.label)
    .slice(0, 2);
  return tags.length ? tags : ['Everyday work & study'];
}

/*
 * `best` decides which end of the range wins; a row without it is never
 * ranked here. Screen size has no `best` on purpose - a 13" and a 15" are a
 * trade-off between portability and workspace, not better and worse.
 * Weight DOES rank, because lighter is not a trade-off in the same way -
 * nobody wants the same machine to be heavier. Features are ranked too, but
 * by featureStandings() rather than a `parse`, because they are a set and
 * not a number.
 *
 * Desktop form factors come through with a score of zero, which makes the
 * build row fail to parse and so go unticked, for the same reason as screen.
 *
 * `header: true` keeps price out of the spec list: it is already the
 * largest thing on the panel.
 *
 * `rank` orders the strengths when there are more than MAX_WINS of them.
 * Features sit fourth on purpose: a touchscreen or a folding hinge decides
 * more purchases than another 128GB of storage, and an earlier version
 * ranked them last, which let storage push a touchscreen off the panel.
 */
const ROWS = [
  {
    key: 'price',
    label: 'Price',
    header: true,
    best: 'low',
    rank: 1,
    parse: parseMoney,
    lead: { best: 'Lowest price', better: 'Lower price' },
    detail: function (item, items, index, isBest) {
      if (!isBest) return '';
      const mine = parseMoney(item.price);
      const dearer = items
        .map((other) => parseMoney(other.price))
        .filter((value) => value !== null && mine !== null && value > mine);
      if (mine === null || dearer.length === 0) return '';
      return formatMoney(Math.min.apply(null, dearer) - mine) + ' less';
    },
  },
  {
    key: 'buildClass',
    label: 'Build',
    desktopLabel: 'Form factor',
    best: 'high',
    rank: 6,
    parse: function (value, item) {
      return Number(item.buildScore) || null;
    },
    note: function (item) {
      return BUILD_NOTES[item.buildClass];
    },
    lead: { best: 'Strongest build', better: 'Stronger build' },
    detail: function (item) {
      return item.buildClass;
    },
  },
  {
    key: 'cpu',
    label: 'Processor',
    best: 'high',
    rank: 2,
    parse: parseCpu,
    lead: { best: 'Fastest processor', better: 'Faster processor' },
    detail: function (item) {
      return item.cpu;
    },
  },
  {
    key: 'ram',
    label: 'RAM',
    best: 'high',
    rank: 3,
    parse: parseSize,
    lead: { best: 'Most RAM', better: 'More RAM' },
    detail: function (item) {
      return item.ram;
    },
  },
  {
    key: 'ssd',
    label: 'Storage',
    best: 'high',
    rank: 8,
    parse: parseSize,
    lead: { best: 'Most storage', better: 'More storage' },
    detail: function (item) {
      return item.ssd;
    },
  },
  { key: 'screen', label: 'Screen' },
  {
    key: 'weight',
    label: 'Weight',
    best: 'low',
    rank: 5,
    value: function (item) {
      const weight = weightOf(item);
      return weight === null ? '' : weight + ' kg';
    },
    parse: function (value, item) {
      return weightOf(item);
    },
    lead: { best: 'Lightest', better: 'Lighter' },
    detail: function (item) {
      const weight = weightOf(item);
      return weight === null ? '' : weight + ' kg';
    },
  },
  {
    key: 'features',
    label: 'Features',
    value: function (item) {
      return featureKeys(item)
        .map((key) => (FEATURE_LABELS[key] ? FEATURE_LABELS[key].label : null))
        .filter(Boolean)
        .join(', ');
    },
  },
  {
    key: 'os',
    label: 'Windows',
    best: 'high',
    rank: 9,
    parse: parseWindows,
    lead: { best: 'Newest Windows', better: 'Newer Windows' },
    detail: function (item) {
      return item.os;
    },
  },
  {
    key: 'condition',
    label: 'Condition',
    best: 'high',
    rank: 7,
    parse: function (value) {
      return CONDITION_RANK[value] || null;
    },
    note: function (item) {
      const detail = gradeDetail(item);
      return detail ? detail.cosmetic : null;
    },
    lead: { best: 'Best condition', better: 'Better condition' },
    detail: function (item) {
      const detail = gradeDetail(item);
      return detail ? item.condition + ', battery ' + detail.battery.toLowerCase() : item.condition;
    },
  },
  {
    key: 'battery',
    label: 'Battery',
    best: 'high',
    value: function (item) {
      if (isDesktop(item)) return '';
      const detail = gradeDetail(item);
      return detail ? detail.battery : '';
    },
    parse: function (value, item) {
      if (isDesktop(item)) return null;
      const detail = gradeDetail(item);
      return detail ? detail.batteryFloor : null;
    },
  },
];

/* Where a feature strength sits against the spec strengths. */
const FEATURE_RANK = 4;

function rowValue(row, item) {
  return (row.value ? row.value(item) : item[row.key]) || '';
}

/* "Build" is the wrong word for a tower next to a micro. */
function rowLabel(row, items) {
  if (row.desktopLabel && items.every(isDesktop)) return row.desktopLabel;
  return row.label;
}

/*
 * For one row: who leads the field outright, and WHICH rivals each machine
 * beats. `winner` stays -1 on a tie, so a tie ticks nobody rather than
 * everybody, but `beats` still credits everyone it genuinely beat - and
 * holding the indices, not just a count, is what lets a partial win name
 * the machine it beat.
 */
function standingsFor(row, items) {
  const blank = { winner: -1, beats: items.map(() => []) };
  if (!row.best) return blank;

  const parsed = items.map(function (item) {
    return row.parse(rowValue(row, item), item);
  });
  if (parsed.some((value) => value === null || value === undefined)) return blank;

  if (parsed[0] && typeof parsed[0] === 'object') {
    const family = parsed[0].family;
    if (parsed.some((value) => value.family !== family)) return blank;
  }

  const scores = parsed.map((value) => (typeof value === 'object' ? value.score : value));
  const wins = row.best === 'low' ? (a, b) => a < b : (a, b) => a > b;

  const beats = scores.map((score, i) =>
    scores.map((other, j) => (j !== i && wins(score, other) ? j : -1)).filter((j) => j !== -1)
  );

  const target = row.best === 'low' ? Math.min.apply(null, scores) : Math.max.apply(null, scores);
  const winner = scores.filter((score) => score === target).length === 1 ? scores.indexOf(target) : -1;

  return { winner: winner, beats: beats };
}

/*
 * Features are a set, not a number, so they cannot be scored by parse().
 * A machine only beats another when it has everything that one has AND
 * something more - a touchscreen against no touchscreen. Two machines with
 * different capabilities (one folds, the other detaches) tick neither,
 * because neither is the better machine, they are different machines.
 */
function featureStandings(items) {
  const sets = items.map(featureKeys);

  const beats = sets.map((mine, i) =>
    sets
      .map((other, j) => {
        if (j === i) return -1;
        const hasAllOfTheirs = other.every((key) => mine.indexOf(key) !== -1);
        return hasAllOfTheirs && mine.length > other.length ? j : -1;
      })
      .filter((j) => j !== -1)
  );

  const clearOf = beats.map((list) => list.length === items.length - 1);
  const winner = clearOf.filter(Boolean).length === 1 ? clearOf.indexOf(true) : -1;

  return { winner: winner, beats: beats };
}

/*
 * Comparing two grades of the same model is a common case here, and then
 * every panel is headed by the same words. Qualify the name with the grade,
 * and with the price if even that matches, so each panel is identifiable.
 */
function displayName(items, index) {
  const item = items[index];
  const sameTitle = items.filter((other) => other.title === item.title);
  if (sameTitle.length < 2) return item.title;

  if (item.condition && sameTitle.filter((other) => other.condition === item.condition).length < 2) {
    return item.title + ' (' + item.condition + ')';
  }
  return item.title + ' (' + item.price + ')';
}

/* "Dell Latitude 5420" -> "Latitude 5420", to keep "than the ..." short. */
function shortName(items, index) {
  const full = displayName(items, index);
  const first = full.split(' ')[0];
  if (VENDOR_WORDS.indexOf(first.toLowerCase()) !== -1) return full.slice(first.length + 1);
  return full;
}

/*
 * What each machine is better at, relative to the others in front of it.
 * A spec counts wherever it beats at least one rival; a feature counts
 * where at least one rival lacks it. The list is then cut to the highest
 * ranked MAX_WINS, so the panel makes a point instead of a list.
 */
function benefitsFor(items, standings) {
  const many = items.length > 2;

  return items.map(function (item, index) {
    const list = [];

    ROWS.forEach((row) => {
      if (!row.lead) return;

      const standing = standings[row.key];
      const beaten = standing.beats[index];
      if (beaten.length === 0) return;

      const isBest = standing.winner === index;
      let lead = isBest && many ? row.lead.best : row.lead.better;

      if (!isBest && many) {
        lead +=
          beaten.length === 1
            ? ' than the ' + shortName(items, beaten[0])
            : ' than ' + beaten.length + ' others';
      }

      list.push({
        rank: row.rank || 9,
        leads: isBest,
        lead: lead,
        detail: row.detail ? row.detail(item, items, index, isBest) : '',
      });
    });

    const mine = featureKeys(item);
    FEATURE_ORDER.forEach((key) => {
      if (mine.indexOf(key) === -1) return;
      const lacking = items.filter((other, i) => i !== index && featureKeys(other).indexOf(key) === -1).length;
      if (lacking === 0) return;
      const label = FEATURE_LABELS[key];
      if (label) list.push({ rank: FEATURE_RANK, leads: true, lead: label.lead, detail: label.detail });
    });

    return list.sort((a, b) => a.rank - b.rank).slice(0, MAX_WINS);
  });
}

function readStore() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writeStore(items) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    /* storage unavailable - the tray still works for this page view */
  }
}

class CompareProducts extends HTMLElement {
  constructor() {
    super();
    this.max = parseInt(this.dataset.max, 10) || 2;
    this.items = readStore().slice(0, this.max);

    this.itemsEl = this.querySelector('[data-compare-items]');
    this.countEl = this.querySelector('[data-compare-count]');
    this.hintEl = this.querySelector('[data-compare-hint]');
    this.openEl = this.querySelector('[data-compare-open]');
    this.tableEl = this.querySelector('[data-compare-table]');
    this.modalEl = this.querySelector('[data-compare-modal]');

    this.onToggleChange = this.onToggleChange.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    document.addEventListener('change', this.onToggleChange);
    document.addEventListener('keydown', this.onKeydown);

    this.openEl.addEventListener('click', () => this.open());
    this.querySelector('[data-compare-clear]').addEventListener('click', () => this.clear());
    this.querySelectorAll('[data-compare-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );

    this.render();
    this.syncToggles();

    /*
     * Dawn dispatches no event when it swaps the grid after a facet change,
     * so watch for the replacement and re-tick the surviving checkboxes.
     */
    const grid = document.getElementById('ProductGridContainer');
    if (grid && 'MutationObserver' in window) {
      this.observer = new MutationObserver(() => this.syncToggles());
      this.observer.observe(grid, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onToggleChange);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.observer) this.observer.disconnect();
  }

  onToggleChange(event) {
    const input = event.target.closest('.compare-toggle__input');
    if (!input) return;

    let data;
    try {
      data = JSON.parse(input.dataset.compareProduct);
    } catch (e) {
      return;
    }

    if (input.checked) {
      this.add(data, input);
    } else {
      this.remove(data.id);
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && !this.modalEl.hidden) this.close();
  }

  add(data, input) {
    if (this.items.some((item) => item.id === data.id)) return;

    /*
     * One kind at a time. Screen size, weight and battery only mean
     * something on a laptop, and upgrade room only means something on a
     * desktop, so a mixed tray would compare them on almost nothing.
     * Items stored before `kind` existed have none, and are left alone.
     */
    const kind = this.items.length ? this.items[0].kind : null;
    if (kind && data.kind && data.kind !== kind) {
      if (input) input.checked = false;
      this.announce(
        kind === 'laptop'
          ? 'You are comparing laptops. Clear the list to compare desktops instead.'
          : 'You are comparing desktops. Clear the list to compare laptops instead.'
      );
      return;
    }

    if (this.items.length >= this.max) {
      /* Undo the tick rather than silently ignoring it. */
      if (input) input.checked = false;
      this.announce('You can compare up to ' + this.max + ' at a time.');
      return;
    }

    this.items.push(data);
    this.persist();
  }

  remove(id) {
    this.items = this.items.filter((item) => item.id !== id);
    this.persist();
  }

  clear() {
    this.items = [];
    this.persist();
    this.close();
  }

  persist() {
    writeStore(this.items);
    this.render();
    this.syncToggles();
  }

  /* Re-tick checkboxes for items in the tray, and untick everything else. */
  syncToggles() {
    const ids = this.items.map((item) => item.id);
    document.querySelectorAll('.compare-toggle__input').forEach((input) => {
      let data;
      try {
        data = JSON.parse(input.dataset.compareProduct);
      } catch (e) {
        return;
      }
      const selected = ids.indexOf(data.id) !== -1;
      input.checked = selected;
      input.closest('.compare-toggle').classList.toggle('compare-toggle--selected', selected);
    });
  }

  announce(message) {
    const existing = this.querySelector('.compare-tray__message');
    if (existing) existing.remove();

    const el = document.createElement('p');
    el.className = 'compare-tray__message';
    el.setAttribute('role', 'status');
    el.textContent = message;
    this.querySelector('.compare-tray').appendChild(el);
    setTimeout(() => el.remove(), 5000);
  }

  render() {
    this.hidden = this.items.length === 0;
    this.countEl.textContent = this.items.length;

    /*
     * A comparison of one is not a comparison - it would render a lone
     * panel claiming it was "matched or beaten on every point". So the
     * button waits, and the hint says what it is waiting for.
     */
    const ready = this.items.length >= MIN_TO_COMPARE;
    this.openEl.disabled = !ready;
    if (this.hintEl) {
      this.hintEl.textContent = ready ? '' : 'Pick one more to compare';
    }

    this.itemsEl.innerHTML = '';
    this.items.forEach((item, index) => {
      const cell = document.createElement('div');
      cell.className = 'compare-tray__item';

      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 48;
        img.height = 48;
        cell.appendChild(img);
      }

      const name = document.createElement('span');
      name.className = 'compare-tray__item-title';
      name.textContent = displayName(this.items, index);
      cell.appendChild(name);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'compare-tray__remove';
      remove.setAttribute('aria-label', 'Remove ' + item.title + ' from comparison');
      remove.textContent = '×';
      remove.addEventListener('click', () => this.remove(item.id));
      cell.appendChild(remove);

      this.itemsEl.appendChild(cell);
    });
  }

  /*
   * Refresh price and availability before showing the panels. Failures are
   * swallowed deliberately: a comparison built from slightly stale price data
   * is much better than no comparison because one fetch timed out.
   */
  async refresh() {
    await Promise.all(
      this.items.map(async (item) => {
        try {
          const response = await fetch('/products/' + item.handle + '.js');
          if (!response.ok) return;
          const live = await response.json();
          item.available = live.available;
          item.price = formatMoney(live.price / 100);
        } catch (e) {
          /* keep the captured values */
        }
      })
    );
    writeStore(this.items);
  }

  async open() {
    if (this.items.length < MIN_TO_COMPARE) return;

    this.tableEl.innerHTML = '<p class="compare-modal__loading">Checking latest prices and stock&hellip;</p>';
    this.modalEl.hidden = false;
    document.body.classList.add('overflow-hidden');

    await this.refresh();
    this.renderPanels();

    const close = this.querySelector('.compare-modal__close');
    if (close) close.focus();
  }

  close() {
    this.modalEl.hidden = true;
    document.body.classList.remove('overflow-hidden');
  }

  /* One self-contained panel per machine: picture, price, fit, strengths, specs. */
  renderPanels() {
    const items = this.items;

    const standings = {};
    ROWS.forEach((row) => {
      standings[row.key] = standingsFor(row, items);
    });
    /* Features are a set, so they get their own comparison. */
    standings.features = featureStandings(items);

    const benefits = benefitsFor(items, standings);

    /* Drop any spec nobody has, so no panel carries a line of dashes. */
    const specRows = ROWS.filter((row) => !row.header && items.some((item) => rowValue(row, item) !== ''));

    /*
     * Which rows match across every machine. Marking these is what lets a
     * shopper skip past the rows that are not the decision - without it,
     * two identical 256 GB figures look the same as two different ones
     * until you read both panels.
     */
    const same = {};
    specRows.forEach((row) => {
      const values = items.map((item) => rowValue(row, item));
      same[row.key] = items.length > 1 && values.every((value) => value !== '' && value === values[0]);
    });

    const grid = document.createElement('div');
    grid.className = 'compare-panels';

    grid.style.setProperty('--compare-count', items.length);
    grid.style.setProperty('--spec-count', specRows.length);
    grid.style.setProperty('--compare-rows', specRows.length + PANEL_FIXED_ROWS);

    items.forEach((item, index) => {
      const panel = document.createElement('article');
      panel.className = 'compare-panel';
      if (item.available === false) panel.classList.add('compare-panel--sold');

      const media = document.createElement('a');
      media.className = 'compare-panel__media';
      media.href = item.url;
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        media.appendChild(img);
      }
      if (item.condition) {
        const pill = document.createElement('span');
        pill.className = 'compare-panel__grade';
        pill.textContent = item.condition;
        media.appendChild(pill);
      }
      panel.appendChild(media);

      const name = document.createElement('a');
      name.className = 'compare-panel__title';
      name.href = item.url;
      name.textContent = displayName(items, index);
      panel.appendChild(name);

      const price = document.createElement('p');
      price.className = 'compare-panel__price';
      price.textContent = item.available === false ? 'Sold' : item.price;
      panel.appendChild(price);

      const idealHeading = document.createElement('p');
      idealHeading.className = 'compare-panel__heading';
      idealHeading.textContent = 'Ideal for';
      panel.appendChild(idealHeading);

      const tags = document.createElement('ul');
      tags.className = 'compare-panel__tags';
      idealFor(item).forEach((label) => {
        const li = document.createElement('li');
        li.textContent = label;
        tags.appendChild(li);
      });
      panel.appendChild(tags);

      const heading = document.createElement('p');
      heading.className = 'compare-panel__heading';
      heading.textContent = 'Better here';
      panel.appendChild(heading);

      if (benefits[index].length === 0) {
        const none = document.createElement('p');
        none.className = 'compare-panel__none';
        none.textContent = 'Matched or beaten on every point.';
        panel.appendChild(none);
      } else {
        const list = document.createElement('ul');
        list.className = 'compare-panel__wins';
        benefits[index].forEach((benefit) => {
          const li = document.createElement('li');
          if (benefit.leads) li.className = 'compare-panel__win--leads';

          const lead = document.createElement('span');
          lead.className = 'compare-panel__lead';
          lead.textContent = benefit.lead;
          li.appendChild(lead);

          if (benefit.detail) {
            const detail = document.createElement('span');
            detail.className = 'compare-panel__detail';
            detail.textContent = benefit.detail;
            li.appendChild(detail);
          }

          list.appendChild(li);
        });
        panel.appendChild(list);
      }

      const specs = document.createElement('dl');
      specs.className = 'compare-panel__specs';

      specRows.forEach((row) => {
        const value = rowValue(row, item);

        const line = document.createElement('div');
        line.className = 'compare-spec';
        if (standings[row.key] && standings[row.key].winner === index) {
          line.classList.add('compare-spec--best');
        }
        if (same[row.key]) line.classList.add('compare-spec--same');

        const dt = document.createElement('dt');
        dt.textContent = rowLabel(row, items);
        line.appendChild(dt);

        const dd = document.createElement('dd');
        const text = document.createElement('span');
        text.className = 'compare-spec__value';
        text.textContent = value || '—';
        dd.appendChild(text);

        if (same[row.key]) {
          const flag = document.createElement('span');
          flag.className = 'compare-spec__same';
          flag.textContent = 'Same';
          dd.appendChild(flag);
        }

        const note = value && row.note ? row.note(item) : null;
        if (note) {
          const small = document.createElement('small');
          small.className = 'compare-spec__note';
          small.textContent = note;
          dd.appendChild(small);
        }

        line.appendChild(dd);
        specs.appendChild(line);
      });

      panel.appendChild(specs);

      const action = document.createElement('a');
      action.href = item.url;
      action.className =
        item.available === false
          ? 'button button--secondary compare-panel__action'
          : 'button compare-panel__action';
      action.textContent = item.available === false ? 'View similar' : 'View this one';
      panel.appendChild(action);

      grid.appendChild(panel);
    });

    this.tableEl.innerHTML = '';
    this.tableEl.appendChild(grid);

    const caveat = document.createElement('p');
    caveat.className = 'compare-caveat';
    caveat.textContent = items.every(isDesktop)
      ? 'A tick marks where one machine is ahead, and "Same" marks a spec they share. Only the three biggest differences are listed. Form factor is never ticked - a tower and a micro are a trade-off between upgrade room and desk space, not better and worse.'
      : 'A tick marks where one machine is ahead, and "Same" marks a spec they share. Only the three biggest differences are listed. Weights are the published figure for the standard build and can vary by around 100g with a touchscreen or a larger battery. Cosmetic and battery figures are the minimums guaranteed for each grade.';
    this.tableEl.appendChild(caveat);
  }
}

if (!customElements.get('compare-products')) {
  customElements.define('compare-products', CompareProducts);
}